// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { LegacyLayoutNode, LegacyTreeNode } from '../../../lib/tree';

interface SizeConfig {
  width: number;
  height: number;
  fontSize: number;
  hGap: number;
  vGap: number;
}

interface TerminalBadgesProps {
  layoutNodes: LegacyLayoutNode[];
  sizeConfig: SizeConfig;
  showCosts: boolean;
  showQALYs: boolean;
  getNodeData: (id: string) => LegacyTreeNode | null;
}

export const TerminalBadges: React.FC<TerminalBadgesProps> = ({
  layoutNodes,
  sizeConfig,
  showCosts,
  showQALYs,
  getNodeData,
}) => {
  return (
    <>
      {layoutNodes
        .filter(node => node.type === 'terminal')
        .map(node => {
          const nodeData = getNodeData(node.id);
          if (!nodeData) return null;
          
          const badges = [];
          // Reduced gap from 12px to 6px to keep terminal blocks visually grouped
          let yOffset = node.y + sizeConfig.height + 6;
          
          // Standardized format: Line 1 (bold): Cost: €600
          if (showCosts && nodeData.cost_eur !== undefined) {
            badges.push(
              <text
                key={`cost-${node.id}`}
                x={node.x + sizeConfig.width / 2}
                y={yOffset}
                textAnchor="middle"
                className="edge-label terminal-outcome-cost"
                stroke="rgba(10,20,40,0.95)"
                strokeWidth={4}
                fill="rgba(255, 255, 255, 0.98)"
                fontSize={sizeConfig.fontSize + 1}
                fontWeight="700"
              >
                Cost: €{nodeData.cost_eur.toFixed(0)}
              </text>
            );
            yOffset += 18;
          }
          
          // Standardized format: Line 2 (regular): QALY: 0.80
          if (showQALYs && nodeData.qaly !== undefined) {
            badges.push(
              <text
                key={`qaly-${node.id}`}
                x={node.x + sizeConfig.width / 2}
                y={yOffset}
                textAnchor="middle"
                className="edge-label terminal-outcome-qaly"
                stroke="rgba(10,20,40,0.9)"
                strokeWidth={3}
                fill="rgba(255, 255, 255, 0.92)"
                fontSize={sizeConfig.fontSize}
                fontWeight="400"
              >
                QALY: {nodeData.qaly.toFixed(2)}
              </text>
            );
          }
          
          return badges;
        })
        .flat()
        .filter(Boolean)}
    </>
  );
};

